import axios from "axios";
import { captureException, addBreadcrumb } from "@/analytics/sentry";

// Debug: Log all available env variables (remove in production)
// if (import.meta.env.DEV) {
//   console.log("🔍 Available Vite env variables:", {
//     MODE: import.meta.env.MODE,
//     VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
//     allKeys: Object.keys(import.meta.env).filter(key => key.startsWith('VITE_'))
//   });
// }

// Get baseURL from environment variable
// Access it directly - Vite automatically exposes VITE_* env variables
const baseURL = import.meta.env.VITE_API_BASE_URL;

// Check if baseURL is missing or empty
if (!baseURL || baseURL.trim() === "") {
  console.error(
    "❌ VITE_API_BASE_URL is not defined in environment variables.\n" +
    "📝 Current env values:", 
    {
      MODE: import.meta.env.MODE,
      VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
      allViteKeys: Object.keys(import.meta.env).filter(key => key.startsWith('VITE_'))
    }, "\n" +
    "📝 Please:\n" +
    "   1. Verify .env file exists in project root\n" +
    "   2. Ensure it contains: VITE_API_BASE_URL=http://localhost:8080/api/v1\n" +
    "   3. RESTART your dev server (npm run dev)\n" +
    "   4. Vite only loads .env files when the server starts!"
  );
}

const instance = axios.create({
  baseURL: baseURL ? baseURL.trim() : "",
  withCredentials: true, // Ensure cookies are sent
});

// Request interceptor to log warnings if baseURL is missing
instance.interceptors.request.use(
  (config) => {
    const currentBaseURL = baseURL ? baseURL.trim() : "";
    if (!currentBaseURL && config.url && !config.url.startsWith('http')) {
      console.warn(
        `⚠️ Making request to "${config.url}" without baseURL.\n` +
        `   VITE_API_BASE_URL is not set. Please check your .env file and restart the dev server.`
      );
    }

    // Add breadcrumb for Sentry
    addBreadcrumb({
      category: "http",
      type: "http",
      data: {
        url: config.url,
        method: config.method,
        baseURL: currentBaseURL,
      },
      level: "info",
    });

    return config;
  },
  (error) => {
    // Capture request error in Sentry
    captureException(error, {
      context: "axios_request",
      url: error.config?.url,
      method: error.config?.method,
    });
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const isProtectedRoute = (url) => {
  try {
    const base = baseURL || window.location.origin;
    const fullUrl = new URL(url, base);
    const pathname = fullUrl.pathname;
    const protectedPatterns = [
      /^\/dashboard/,
      /^\/user/,
      /^\/transactions/,
      /^\/auth\/check/,
      /^\/orders/,
      /^\/admin/,
    ];
    return protectedPatterns.some((pattern) => pattern.test(pathname));
  } catch (error) {
    console.warn("isProtectedRoute failed for:", url, error);
    return false;
  }
};

// ✅ Response interceptor — refresh token logic
instance.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (!error.response || !originalRequest) {
      console.warn("No response or config. Possible network or CORS error.");
      // Capture network/CORS errors in Sentry
      captureException(error, {
        context: "axios_response",
        type: "network_error",
        url: originalRequest?.url,
        method: originalRequest?.method,
      });
      return Promise.reject(error);
    }

    const isProtected = isProtectedRoute(originalRequest.url);

    // Log API errors to Sentry (excluding 401 which are handled separately)
    if (error.response.status >= 500) {
      // Server errors (5xx)
      captureException(error, {
        context: "axios_response",
        type: "server_error",
        status: error.response.status,
        url: originalRequest.url,
        method: originalRequest.method,
        responseData: error.response.data,
      });
    } else if (error.response.status >= 400 && error.response.status !== 401) {
      // Client errors (4xx) except 401
      addBreadcrumb({
        category: "http",
        type: "http",
        data: {
          url: originalRequest.url,
          method: originalRequest.method,
          status: error.response.status,
          responseData: error.response.data,
        },
        level: "warning",
      });
    }

    if (
      error.response.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes("/auth/refresh-token") &&
      isProtected
    ) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: () => resolve(instance(originalRequest)),
            reject: err => reject(err),
          });
        });
      }

      isRefreshing = true;

      try {
        await instance.post("/auth/refresh-token");
        processQueue(null);
        return instance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        // Capture refresh token errors
        captureException(refreshError, {
          context: "axios_response",
          type: "token_refresh_error",
          url: originalRequest.url,
        });

        if (
          isProtected &&
          window.location.pathname !== "/auth/login"
        ) {
          // window.location.href = "/auth/login";
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default instance;
